import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Table } from "react-bootstrap";
import classRoomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import BackButton from "../../../common/BackButton";
import TablePagination from "../../../common/pagination/TablePagination";
import TableError from "../../common/TableError";
import TableSkeleton from "../../common/TableSkeleton";
import { useGetSentNotificationList } from "../../../../hooks/organization/notification/useGetSentNotificationList";
import { useGetNotificationReceiverList } from "../../../../hooks/organization/notification/useGetNotificationReceiverList";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import "./NotificationDetail.css";

const NotificationDetail = () => {
  const { batchId } = useParams();
  const [receiverPage, setReceiverPage] = useState(1);
  const [loadedImages, setLoadedImages] = useState({});

  // Fetch notification detail
  const {
    notifications,
    loading: detailLoading,
    error: detailError,
    getSentNotifications,
  } = useGetSentNotificationList();

  // Fetch receivers
  const {
    receivers,
    meta: receiverMeta,
    loading: receiverLoading,
    error: receiverError,
    getNotificationReceiverList,
  } = useGetNotificationReceiverList();

  useEffect(() => {
    if (batchId) {
      getSentNotifications({ batch_id: batchId });
    }
  }, [batchId]);

  useEffect(() => {
    if (batchId) {
      getNotificationReceiverList({ batch_id: batchId, page: receiverPage });
    }
  }, [batchId, receiverPage]);

  const handleReceiverRefresh = () => {
    getNotificationReceiverList({ batch_id: batchId, page: receiverPage });
  };

  // Get the first (and only) notification from the filtered response
  const notification = notifications?.[0] || null;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (seenAt, readAt, dismissedAt) => {
    if (dismissedAt)
      return (
        <span className="notification-badge badge-dismissed">Dismissed</span>
      );
    if (readAt)
      return <span className="notification-badge badge-read">Read</span>;
    if (seenAt)
      return <span className="notification-badge badge-seen">Seen</span>;
    return <span className="notification-badge badge-unread">Unread</span>;
  };

  const formatRoleName = (roleSlug) => {
    if (!roleSlug) return "-";
    return roleSlug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isImageFile = (ext) => {
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(
      ext?.toLowerCase(),
    );
  };

  const totalReceiverPages = receiverMeta?.last_page || 1;

  return (
    <div className="notification-detail-cnt">
      <BackButton fallbackPath="/organization/notification" />

      {/* Top Banner */}
      <div className="classroom-cnt">
        <div className="classroom-container">
          <img src={classRoomImg} className="img" alt="Notification Detail" />
          <h2 className="text">Notification Detail</h2>
        </div>
      </div>

      {/* Notification Info Card */}
      {detailLoading && notifications === undefined ? (
        <div className="notification-info-card">
          <div
            className="skeleton-box"
            style={{ width: "60%", height: 24, marginBottom: 12 }}
          ></div>
          <div
            className="skeleton-box"
            style={{ width: "100%", height: 16, marginBottom: 8 }}
          ></div>
          <div
            className="skeleton-box"
            style={{ width: "40%", height: 14 }}
          ></div>
        </div>
      ) : detailError ? (
        <div className="alert alert-danger mt-3">{detailError}</div>
      ) : notification ? (
        <div className="notification-info-card">
          <h3 className="notification-info-title">{notification.title}</h3>
          <p className="notification-info-description">
            {notification.description}
          </p>

          {/* Media Attachments */}
          {Array.isArray(notification.media) &&
            notification.media.length > 0 && (
              <div className="notification-media-section">
                <h6 className="media-section-title">
                  <i className="fa-solid fa-paperclip"></i> Attachments
                </h6>
                <div className="notification-media-grid">
                  {notification.media.map((item, idx) =>
                    isImageFile(item.extension) ? (
                      <a
                        key={idx}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="media-image-wrapper"
                      >
                        {!loadedImages[idx] && (
                          <div className="media-image-skeleton"></div>
                        )}
                        <img
                          src={item.url}
                          alt={item.name}
                          className="media-image"
                          style={{
                            display: loadedImages[idx] ? "block" : "none",
                          }}
                          onLoad={() =>
                            setLoadedImages((prev) => ({
                              ...prev,
                              [idx]: true,
                            }))
                          }
                        />
                      </a>
                    ) : (
                      <a
                        key={idx}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="media-file-link"
                      >
                        <i className="fa-solid fa-file"></i>
                        <span>{item.name}</span>
                      </a>
                    ),
                  )}
                </div>
              </div>
            )}

          <div className="notification-info-meta">
            <span className="notification-meta-item">
              <i className="fa-regular fa-calendar"></i>
              {formatDate(notification.created_at)}
            </span>
            <span className="notification-meta-item">
              <i className="fa-regular fa-user"></i>
              {notification.total_recipients} Recipients
            </span>
            {Array.isArray(notification.receiver_roles) &&
              notification.receiver_roles.length > 0 && (
                <span className="notification-meta-item">
                  <i className="fa-regular fa-id-badge"></i>
                  {notification.receiver_roles.map(formatRoleName).join(", ")}
                </span>
              )}
          </div>

          {/* Stats Row */}
          <div className="notification-stats-row">
            <div className="notification-stat-item stat-read">
              <span className="stat-number">{notification.total_read}</span>
              <span className="stat-label">Read</span>
            </div>
            <div className="notification-stat-item stat-unread">
              <span className="stat-number">{notification.total_unread}</span>
              <span className="stat-label">Unread</span>
            </div>
            <div className="notification-stat-item stat-dismissed">
              <span className="stat-number">
                {notification.total_dismissed}
              </span>
              <span className="stat-label">Dismissed</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-info mt-3">Notification not found.</div>
      )}

      {/* Receivers Table */}
      <div className="notification-receivers-section">
        <h4 className="receivers-title">
          <i className="fa-solid fa-users"></i> Recipients
        </h4>

        {receiverLoading && receivers === undefined ? (
          <TableSkeleton rows={ITEMS_PER_PAGE} columns={5} />
        ) : receiverError || receivers === null ? (
          <TableError
            error={receiverError}
            handleRefresh={handleReceiverRefresh}
            loading={receiverLoading}
          />
        ) : receivers.length === 0 ? (
          <div className="alert alert-info mt-3">No recipients found.</div>
        ) : (
          <div className="custom-table-container mt-3">
            <div className="custom-table-wrapper">
              <Table hover className="custom-table">
                <thead className="custom-table-head">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Role</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {receivers.map((receiver, index) => (
                    <tr key={receiver.id}>
                      <td>{(receiverPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                      <td>{receiver.name || "-"}</td>
                      <td style={{ textTransform: "capitalize" }}>
                        {receiver.type || "-"}
                      </td>
                      <td>{formatRoleName(receiver.role_slug)}</td>
                      <td>
                        {getStatusBadge(
                          receiver.seen_at,
                          receiver.read_at,
                          receiver.dismissed_at,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <TablePagination
              totalPage={totalReceiverPages}
              currentPage={receiverPage}
              onPageChange={setReceiverPage}
              disabled={receiverLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationDetail;
